import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { useParams } from 'react-router-dom';

import { Permission, useUserPermissions } from '@/hooks/useUserPermissions';
import { AllDevices } from '@/Structures/AllDevices';
import { edit as editStructure } from '@/Structures/api';
import { SubscribersView } from '@/Structures/subscribers';
import { TenantAlertsTable } from '@/Structures/subscribers/alerts';
import { useStructure } from '@/Structures/useStructures';
import { UtilityWaterBills } from '@/Structures/UtilityWaterBills';
import { UsageTab } from '@/Structures/UtilityWaterBills/usage';

import { PaymentSettings } from '../components/PaymentSettings';
import { StructureShell } from '../components/Shell';

export default function NonBilling() {
  const theme = useTheme();
  const { structureId } = useParams<{ structureId: string }>();
  const { structure, mutate: mutateStructure } = useStructure(structureId!);
  const { hasPermission, loading: permissionsLoading } = useUserPermissions();
  const [tab, setTab] = React.useState<'1' | '2' | '3' | '4' | '5'>('1');

  // Handler for updating payment settings
  const handlePaymentSettingsUpdate = React.useCallback(
    async (
      payOnlineUrl: string | null,
      paymentInstructions: string | null,
      dueDateDays: number | null,
    ) => {
      if (!structure) return;
      const updatedStructure = await editStructure({
        ...structure,
        pay_online_url: payOnlineUrl,
        payment_instructions: paymentInstructions,
        due_date_days: dueDateDays,
      });
      // Update the local cache with the new structure data
      mutateStructure(updatedStructure);
    },
    [structure, mutateStructure],
  );

  // Permissions - billing_manager restrictions
  // During loading, assume user has permissions (optimistic) to prevent flickering disabled state
  const canViewUsage = permissionsLoading || hasPermission(Permission.VIEW_USAGE);
  const canManageTenantAlerts =
    permissionsLoading || hasPermission(Permission.MANAGE_TENANT_ALERTS);

  return (
    <StructureShell>
      <TabContext value={tab}>
        <TabList
          onChange={(_e, v) => setTab(v as any)} // change the little underline:
          textColor="primary"
          indicatorColor="primary"
          sx={{
            // give the whole TabList a grey bottom-border:
            borderBottom: 2,
            borderColor: 'grey.300',

            // style the sliding indicator so it overlaps exactly:
            '& .MuiTabs-indicator': {
              height: 3, // your blue line thickness
              backgroundColor: theme.palette.primary.main,
              zIndex: 1, // above the border
            },
          }}
        >
          <Tab label="Billing" value="1" />
          {!canViewUsage ? (
            <Tooltip
              title="You don't have permission to view usage data. Please contact your account owner."
              arrow
            >
              <span>
                <Tab label="Usage" value="2" disabled />
              </span>
            </Tooltip>
          ) : (
            <Tab label="Usage" value="2" />
          )}
          <Tab label="Devices" value="3" />
          <Tab label="Tenants" value="4" />
          {!canManageTenantAlerts ? (
            <Tooltip
              title="You don't have permission to manage tenant alerts. Please contact your account owner."
              arrow
            >
              <span>
                <Tab label="Tenant Alerts" value="5" disabled />
              </span>
            </Tooltip>
          ) : (
            <Tab label="Tenant Alerts" value="5" />
          )}
        </TabList>

        <TabPanel value="1">
          <UtilityWaterBills
            stripeConnect={false}
            payOnlineUrl={structure?.pay_online_url}
            paymentInstructions={structure?.payment_instructions}
            onPaymentSettingsUpdate={handlePaymentSettingsUpdate}
          />
          {structure && (
            <PaymentSettings
              payOnlineUrl={structure.pay_online_url}
              paymentInstructions={structure.payment_instructions}
              dueDateDays={structure.due_date_days}
              hasStripeConnect={false}
              onUpdate={handlePaymentSettingsUpdate}
            />
          )}
        </TabPanel>

        <TabPanel value="2">
          {canViewUsage ? (
            <UsageTab timeZone={structure?.timezone_key!} />
          ) : (
            <div>You don't have permission to view usage data.</div>
          )}
        </TabPanel>

        <TabPanel value="3">
          <AllDevices />
        </TabPanel>

        <TabPanel value="4">
          <SubscribersView stripeConnect={false} />
        </TabPanel>

        <TabPanel value="5">
          {canManageTenantAlerts && structure ? (
            <TenantAlertsTable />
          ) : (
            <div>You don't have permission to manage tenant alerts.</div>
          )}
        </TabPanel>
      </TabContext>
    </StructureShell>
  );
}
